import React, { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { BulkCreateBody } from '../bulk-work-create-list/BulkCreateBody.tsx';
import { BulkCreateHeader } from '../bulk-work-create-common/BulkCreateHeader.tsx';
import { BulkCreateFooter } from '../bulk-work-create-common/BulkCreateFooter.tsx';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper.tsx';
import {
	BULK_ISSUE_CREATE_SOURCE,
	useBulkCreateLoggingEvents,
} from '../../utils/useBulkCreateLoggingEvents.tsx';
import {
	useBulkCreateContext,
	ErrorMessageMap,
	ErrorStates,
	AiStates,
} from '../../providers/BulkCreateContextProvider.tsx';
import { useGetIssueInsertionActions } from '../../services/useGetIssueInsertionActions.tsx';
import { WorkItemCreationError } from '../work-create-common/index.tsx';

import { IssueCreateAiLoading } from './IssueCreateAiLoading.tsx';
import { RainbowLoadingSidebar } from './RainbowLoadingSidebar.tsx';

const contentStyles = xcss({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	flexWrap: 'nowrap',
});

export const AiBulkDefaultView = ({ onClose }: { onClose: () => void }): React.JSX.Element => {
	const { logCancelAiPanel } = useBulkCreateLoggingEvents();
	const { addSingleIssuesToPage } = useGetIssueInsertionActions();
	const [{ aiState, error }, actions] = useBulkCreateContext();

	const onPanelClose = useCallback(() => {
		logCancelAiPanel();
		addSingleIssuesToPage();
		/*
			Once the confluence-bulk-work-create-editor experiment is enabled we'll be resetting
			the bulk create state in BulkCreateSidePanel's handleOnClose method instead of here.
			We should clean up this code when cleaning up the experiment.
		*/
		if (!expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
			void actions.resetAIBulkCreateState();
		}
		onClose();
	}, [logCancelAiPanel, onClose, addSingleIssuesToPage, actions]);

	const isLoading = aiState === AiStates.LOADING;
	const isError = error !== ErrorStates.EMPTY;
	const isAiIssueGenerationError = error === ErrorStates.ISSUE_GENERATION;
	const isComplete = !isLoading && !isError;

	// When gate is ON: show error screen if not loading & error is not with AI issue generation
	// When gate is OFF: all errors shown here
	// Will rename variable during FG cleanup - logic currently differs between fg ON and OFF
	const shouldShowError =
		isError &&
		(fg('confluence-ai-jira-work-create-fast-follows')
			? !isLoading && !isAiIssueGenerationError
			: true);

	// When gate is ON: show BulkCreateBody if not loading & error screen is not shown
	// We separate the logic for showing the body and footer as we do not want to show the footer if the
	// AI issue generation error is shown, even though the body is shown.
	// When gate is OFF: only show BulkCreateBody if not loading & there is no error
	// Will rename variable during FG cleanup - logic currently differs between fg ON and OFF
	const shouldShowBody = fg('confluence-ai-jira-work-create-fast-follows')
		? !isLoading && !shouldShowError
		: isComplete;

	return (
		<SidePanelWrapper>
			<RainbowLoadingSidebar isShown={isLoading}>
				<Stack xcss={contentStyles}>
					<BulkCreateHeader onClose={onPanelClose} />
					{isLoading && <IssueCreateAiLoading loadingType="bulk" />}
					{shouldShowError && (
						<WorkItemCreationError
							creationSource={BULK_ISSUE_CREATE_SOURCE}
							errorMessage={ErrorMessageMap[error]()}
							isCreatedWithAi
						/>
					)}
					{shouldShowBody && <BulkCreateBody />}
					{isComplete && <BulkCreateFooter onClose={onPanelClose} onCreateAll={onClose} />}
				</Stack>
			</RainbowLoadingSidebar>
		</SidePanelWrapper>
	);
};
